import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Table, Alert, Button } from 'react-bootstrap';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { saveAs } from 'file-saver';

const OrderDetail = () => {
  const { orderNumber } = useParams(); // 使用 orderNumber 而不是 id
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://shopapi.nogi46.me/api/orders/${orderNumber}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setOrder(response.data);
        document.title = `訂單詳情 - ${response.data.orderNumber}`;
      } catch (error) {
        setError('無法獲取訂單詳情，請稍後再試。');
      }
    };

    fetchOrder();
  }, [orderNumber]);

  const generatePdf = async () => {
    const fontUrl = `${window.location.origin}/fonts/NotoSansTC-Regular.ttf`;
    const logoUrl = `${window.location.origin}/images/logo.png`;
    const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer());
    const logoBytes = await fetch(logoUrl).then((res) => res.arrayBuffer());

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const notoFont = await pdfDoc.embedFont(fontBytes);
    const logoImage = await pdfDoc.embedPng(logoBytes);

    const page = pdfDoc.addPage([600, 750]);
    const { width, height } = page.getSize();
    const fontSize = 12;

    // 添加 logo 圖片
    const logoDims = logoImage.scale(0.1);
    page.drawImage(logoImage, {
      x: width - logoDims.width - 30,
      y: height - logoDims.height - 30,
      width: logoDims.width,
      height: logoDims.height,
    });

    // 添加訂單詳情
    page.drawText('訂單詳情', { x: 50, y: height - 50, size: 20, font: notoFont, color: rgb(0, 0, 0) });
    page.drawText(`訂單編號: ${order.orderNumber}`, { x: 50, y: height - 80, size: fontSize, font: notoFont });
    page.drawText(`用戶ID: ${order.userId}`, { x: 50, y: height - 100, size: fontSize, font: notoFont });
    page.drawText(`創建時間: ${new Date(order.createdAt).toLocaleString()}`, { x: 50, y: height - 140, size: fontSize, font: notoFont });
    page.drawText(`用戶名: ${order.user.username}`, { x: 50, y: height - 160, size: fontSize, font: notoFont });
    page.drawText(`用戶郵箱: ${order.user.email}`, { x: 50, y: height - 180, size: fontSize, font: notoFont });
    page.drawText(`用戶電話: ${order.user.phone}`, { x: 50, y: height - 200, size: fontSize, font: notoFont });

    // 賣家資訊
    page.drawText('賣家資訊:', { x: 50, y: height - 240, size: fontSize, font: notoFont });
    page.drawText('E.Y. Studio', { x: 50, y: height - 260, size: fontSize, font: notoFont });
    page.drawText('admin@nogi46.me', { x: 50, y: height - 280, size: fontSize, font: notoFont });
    page.drawText('10699 台北六張犁郵局第109號信箱', { x: 50, y: height - 300, size: fontSize, font: notoFont });

    // 添加產品列表標題
    let yPosition = height - 340;
    page.drawText('產品列表', { x: 50, y: yPosition, size: 16, font: notoFont, color: rgb(0, 0, 0) });
    yPosition -= 20;

    // 添加產品列表
    order.products.forEach(product => {
      page.drawText(`產品ID: ${product.productId}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
      yPosition -= 20;
      page.drawText(`名稱: ${product.name}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
      yPosition -= 20;
      page.drawText(`數量: ${product.quantity}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
      yPosition -= 20;
      page.drawText(`價格: $${product.price}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
      yPosition -= 20;
      if (product.size) {
        page.drawText(`尺寸: ${product.size}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
        yPosition -= 20;
      }
      if (product.member) {
        page.drawText(`成員: ${product.member}`, { x: 50, y: yPosition, size: fontSize, font: notoFont });
        yPosition -= 20;
      }
      yPosition -= 20; // 增加一點空白區域
    });

    // 在底部添加總金額，用粗體和較大的字體顯示
    page.drawText(`總金額: $${order.totalAmount}`, {
      x: 50,
      y: yPosition - 40, // 總金額的位置設置在產品列表下方
      size: 16, // 使用較大的字體
      font: notoFont,
      color: rgb(0, 0, 0),
      opacity: 1.0,
    });

    // 生成PDF並下載
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `order_${order.orderNumber}.pdf`;
    link.click();
  };


  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!order) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <h1>訂單詳情</h1>
      <Row>
        <Col>
          <Table bordered>
            <tbody>
              <tr>
                <th>訂單編號</th>
                <td>{order.orderNumber}</td>
              </tr>
              <tr>
                <th>用戶ID</th>
                <td>{order.userId}</td>
              </tr>
              <tr>
                <th>總金額</th>
                <td>${order.totalAmount}</td>
              </tr>
              <tr>
                <th>創建時間</th>
                <td>{new Date(order.createdAt).toLocaleString()}</td>
              </tr>
              <tr>
                <th>用戶名</th>
                <td>{order.user.username}</td>
              </tr>
              <tr>
                <th>用戶郵箱</th>
                <td>{order.user.email}</td>
              </tr>
              <tr>
                <th>用戶電話</th>
                <td>{order.user.phone}</td>
              </tr>
            </tbody>
          </Table>
          <h2>產品列表</h2>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>產品ID</th>
                <th>名稱</th>
                <th>數量</th>
                <th>價格</th>
                <th>尺寸</th>
                <th>成員</th>
              </tr>
            </thead>
            <tbody>
              {order.products.map((product, index) => (
                <tr key={index}>
                  <td>{product.productId}</td>
                  <td>{product.name}</td>
                  <td>{product.quantity}</td>
                  <td>${product.price}</td>
                  <td>{product.size || ''}</td>
                  <td>{product.member || ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button onClick={generatePdf}>生成帳單 PDF</Button>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderDetail;
